/**
 * Shared header component for Raven Resume.
 * Renders header with app name, Raven bird icon, theme toggle, and auth section.
 *
 * Usage:
 *   import { AppHeader } from './lib/AppHeader.js';
 *   const header = new AppHeader({ appName: 'Resume Builder', version: '1.0.0', onSignOut: () => auth.signOut() });
 *   header.setUser('user@example.com', 'admin', 'Admin');
 */

const RAVEN_BIRD_SVG = `<svg viewBox="0 0 32 32" width="28" height="28" fill="currentColor" style="vertical-align: middle; margin-right: 6px;">
    <path d="M8 4c1 0 3 1 4 3 1.5 2.5 1 5 1 5s2-1 4-1c3 0 5 2 6 4 1 2.5 0 5-1 6l-2 2c-1 1-3 2-5 2h-3l-4 4v-4c-2-1-4-3-4-6 0-2 1-4 2-5l-1-2c-1-1.5 0-3 1-3h2z"/>
    <circle cx="10" cy="11" r="1.2" fill="var(--bg-primary, #1e2530)"/>
    <path d="M5 8c-1-1-2-1-2 0s1 2 2 2" fill="currentColor"/>
</svg>`;

export class AppHeader {
    constructor({ appName = '', version = '', onSignOut = null, onThemeChange = null } = {}) {
        this._appName = appName;
        this._version = version;
        this._onSignOut = onSignOut;
        this._onThemeChange = onThemeChange;

        this._render();
        this._initTheme();
    }

    /** Show user info and sign-out button. */
    setUser(email, role = '', roleLabel = '') {
        const badge = role && roleLabel
            ? ` <span class="role-badge role-${role}">${roleLabel}</span>`
            : '';
        this._userEmailEl.innerHTML = `${email}${badge}`;
        this._signOutBtn.style.display = 'inline-flex';
    }

    /** Show a tenant badge in the header (for non-default tenants). */
    setTenantBadge(tenantSlug) {
        if (!tenantSlug) return;
        const badge = document.createElement('span');
        badge.className = 'tenant-badge';
        badge.textContent = tenantSlug;
        badge.style.cssText = 'margin-left:8px;padding:2px 8px;border-radius:4px;font-size:0.75rem;background:var(--accent,#a0051b);color:#fff;text-transform:uppercase;letter-spacing:0.5px;';
        this._userEmailEl.parentElement.insertBefore(badge, this._userEmailEl);
    }

    /** Hide user info and sign-out button. */
    clearUser() {
        this._userEmailEl.textContent = '';
        this._signOutBtn.style.display = 'none';
    }

    _render() {
        const versionLabel = this._version ? ` v${this._version}` : '';

        const header = document.createElement('header');
        header.innerHTML = `
            <div class="logo">
                ${RAVEN_BIRD_SVG}Raven <span>${this._appName}${versionLabel}</span>
            </div>
            <div class="auth-section">
                <span class="user-email" id="userEmail"></span>
                <div class="theme-toggle" role="switch" tabindex="0" aria-label="Toggle dark theme">
                    <span class="theme-toggle-icon">&#9788;</span>
                    <div class="theme-toggle-switch" id="themeSwitch"></div>
                    <span class="theme-toggle-icon">&#9790;</span>
                </div>
                <button class="btn btn-secondary btn-sm" id="logoutBtn" style="display: none;">Sign Out</button>
            </div>
        `;

        document.body.prepend(header);

        this._userEmailEl = header.querySelector('#userEmail');
        this._signOutBtn = header.querySelector('#logoutBtn');
        this._themeSwitch = header.querySelector('#themeSwitch');
        this._themeWrapper = header.querySelector('.theme-toggle');

        if (this._onSignOut) {
            this._signOutBtn.addEventListener('click', () => this._onSignOut());
        }
    }

    _initTheme() {
        const saved = localStorage.getItem('raven-theme');
        if (saved) {
            this._setTheme(saved === 'dark');
        }

        this._themeSwitch.addEventListener('click', () => {
            this._setTheme(document.documentElement.getAttribute('data-theme') !== 'dark');
        });

        this._themeWrapper.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this._themeSwitch.click();
            }
        });
    }

    /** Apply theme from Firestore after login (overrides localStorage if different). */
    applyThemePreference(theme) {
        if (!theme) return;
        const current = document.documentElement.getAttribute('data-theme');
        if (theme !== current) {
            this._setTheme(theme === 'dark');
        }
    }

    _setTheme(dark) {
        document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
        this._themeWrapper.setAttribute('aria-checked', dark);
        localStorage.setItem('raven-theme', dark ? 'dark' : 'light');
        this._onThemeChange?.(dark ? 'dark' : 'light');
    }
}
